import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useRef } from 'react';
import { Button } from '@mui/material';
import JWT from '../../../helper/jwt.helper';

// ============================================
// CUSTOM BUTTON VERSION - December 1, 2025
// Uses GoogleLogin for idToken but with custom button
// ============================================

const ButtonGoogleSSO = ({ strategy, onSubmit, onError, loading }) => {
  const googleButtonRef = useRef(null);

  const handleCredentialResponse = (credentialResponse) => {
    try {
      const idToken = credentialResponse.credential;

      if (!idToken) {
        console.error('Google SSO: No credential in response');
        onError({ message: 'No ID token received from Google' });
        return;
      }

      // Decode the ID token to get user info
      const { error, data: decoded } = JWT.decode(idToken);

      if (error || !decoded || !decoded.email) {
        console.error('Google SSO: Failed to decode ID token', error);
        onError({ message: 'Invalid ID token from Google' });
        return;
      }

      // Send email and idToken to backend
      onSubmit({
        email: decoded.email,
        idToken: idToken
      });
    } catch (error) {
      console.error('❌ EXCEPTION in handleCredentialResponse:', error);
      onError({ message: 'Error processing Google login' });
    }
  };

  const handleGoogleError = (error) => {
    console.error('❌ Google OAuth error:', error);
    onError({ message: 'Google login failed' });
  };

  const handleCustomButtonClick = () => {
    // Trigger the hidden Google OAuth button
    const googleBtn = googleButtonRef.current?.querySelector('div[role="button"]');
    if (googleBtn) {
      googleBtn.click();
    } else {
      console.error('Google SSO: Could not find OAuth button');
    }
  };

  return (
    <GoogleOAuthProvider clientId={strategy.config.clientId}>
      <div style={{ position: 'relative', width: '100%' }}>
        {/* Botón personalizado visible */}
        <Button
          variant='outlined'
          onClick={handleCustomButtonClick}
          disabled={loading}
          fullWidth
          sx={{
            textTransform: 'none',
            borderColor: '#dadce0',
            color: '#3c4043',
            fontWeight: 500,
            fontSize: '14px',
            padding: '10px 24px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f8f9fa',
              borderColor: '#dadce0',
              cursor: 'pointer'
            },
            '&:disabled': {
              cursor: 'not-allowed'
            }
          }}
        >
          <div className='flex items-center justify-start'>
            <div className='btn-icon btn-icon-google'></div>
            <div className='text-icon initial-case'>{strategy.label ?? 'Sign in with Google'} </div>
          </div>
        </Button>

        {/* GoogleLogin oculto pero funcional - este obtiene el idToken */}
        <div
          ref={googleButtonRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '1px',
            height: '1px',
            opacity: 0,
            overflow: 'hidden',
            visibility: 'hidden'
          }}
        >
          <GoogleLogin
            onSuccess={handleCredentialResponse}
            onError={handleGoogleError}
            useOneTap={false}
            auto_select={false}
            ux_mode='popup'
            context='signin'
            theme='outline'
            size='large'
            text='signin_with'
            shape='rectangular'
            logo_alignment='left'
            width='400'
          />
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default ButtonGoogleSSO;
